import React, { useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { apiService } from '../../services/api';
import { Provider, FilterOptions, ProvidersResponse } from '../../types';
import { FilterPanel } from './FilterPanel';
import { ProviderCard } from './ProviderCard';
import { Pagination } from './Pagination';

// Group providers by NPI and calculate aggregates
const groupProvidersByNPI = (providers: Provider[]) => {
  const grouped = providers.reduce((acc, provider) => {
    const npi = provider.npi;
    if (!acc[npi]) {
      acc[npi] = {
        ...provider,
        services: [provider.services],
        totalBeneficiaries: Number(provider.services.totalBeneficiaries) || 0,
        totalServices: Number(provider.services.totalServices) || 0,
        totalPayment: Number(provider.services.avgMedicarePayment) || 0,
        totalCharge: (Number(provider.services.avgSubmittedCharge) || 0) * (Number(provider.services.totalServices) || 0),
        serviceCount: 1
      };
    } else {
      acc[npi].services.push(provider.services);
      acc[npi].totalBeneficiaries += Number(provider.services.totalBeneficiaries) || 0;
      acc[npi].totalServices += Number(provider.services.totalServices) || 0;
      acc[npi].totalPayment += Number(provider.services.avgMedicarePayment) || 0;
      acc[npi].totalCharge += (Number(provider.services.avgSubmittedCharge) || 0) * (Number(provider.services.totalServices) || 0);
      acc[npi].serviceCount += 1;
    }
    return acc;
  }, {} as Record<string, any>);

  return Object.values(grouped);
};

export const ProviderSearch: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [groupedProviders, setGroupedProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentFilters, setCurrentFilters] = useState<FilterOptions | null>(null);
  const [pageSize, setPageSize] = useState(50);
  const [searchResults, setSearchResults] = useState<ProvidersResponse | null>(null);

  const handleFilter = useCallback(async (filters: FilterOptions, page: number = 1) => {
    setLoading(true);
    setError(null);
    setCurrentPage(page);
    setPageSize(filters.limit || 50);
    
    try {
      const paginatedFilters = {
        ...filters,
        offset: (page - 1) * (filters.limit || 50),
        limit: filters.limit || 50
      };
      
      setCurrentFilters(paginatedFilters);
      
      const response = await apiService.searchProviders(paginatedFilters);
      setSearchResults(response);
      setProviders(response.providers);
      
      // Group providers by NPI
      const grouped = groupProvidersByNPI(response.providers);
      setGroupedProviders(grouped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch providers');
      setProviders([]);
      setGroupedProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePageChange = (page: number) => {
    if (currentFilters) {
      handleFilter(currentFilters, page);
    }
  };

  return (
    <div className="space-y-6">
      <FilterPanel onFilter={handleFilter} loading={loading} />
      
      {error && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-muted-foreground">Loading providers...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && !error && groupedProviders.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Found {groupedProviders.length} unique providers ({providers.length} total services)
            </div>
            
            {searchResults && searchResults.hasMore && (
              <Pagination
                currentPage={currentPage}
                hasMore={searchResults.hasMore}
                onPageChange={handlePageChange}
              />
            )}
          </div>
          
          <div className="grid gap-4">
            {groupedProviders.map((provider, index) => (
              <ProviderCard 
                key={`${provider.npi}-${index}`} 
                provider={provider}
                grouped={true}
              />
            ))}
          </div>

          {searchResults && searchResults.hasMore && (
            <Pagination
              currentPage={currentPage}
              hasMore={searchResults.hasMore}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {!loading && !error && providers.length === 0 && currentFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <h3 className="text-lg font-semibold mb-2">No Providers Found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search criteria or filters.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
